import React from 'react';

interface TableColumn<T> {
  key: keyof T | string;
  label: string;
  render?: (value: any, item: T) => React.ReactNode;
  sortable?: boolean;
  className?: string;
}

interface TableProps<T> {
  readonly data: T[];
  readonly columns: TableColumn<T>[];
  readonly emptyMessage?: string;
  readonly emptyIcon?: React.ReactNode;
  readonly className?: string;
  readonly onRowClick?: (item: T) => void;
}

/**
 * Table - Reusable table component
 * 
 * A flexible table component that can display any type of data with customizable columns,
 * sorting, and empty states. Supports custom rendering for each column.
 * 
 * @param data - Array of data items to display
 * @param columns - Array of column configurations
 * @param emptyMessage - Message to show when data is empty
 * @param emptyIcon - Icon to show when data is empty
 * @param className - Additional CSS classes
 * @param onRowClick - Function to call when a row is clicked
 */
function Table<T>({
  data,
  columns,
  emptyMessage = "No data available",
  emptyIcon,
  className = "",
  onRowClick
}: TableProps<T>) {
  const defaultEmptyIcon = (
    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );

  if (data.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
          {emptyIcon || defaultEmptyIcon}
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No items found</h3>
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  const getValue = (item: T, key: keyof T | string): any => {
    if (typeof key === 'string' && key.includes('.')) {
      // Support nested object access like 'user.name'
      return key.split('.').reduce((obj: any, k) => obj?.[k], item);
    }
    return item[key as keyof T];
  };

  const getRowKey = (item: T, index: number): string => {
    // Try to use 'id' field if available, otherwise use a combination of values
    const itemAsRecord = item as Record<string, any>;
    if (itemAsRecord.id !== undefined) {
      return `row-${itemAsRecord.id}`;
    }
    if (itemAsRecord._id !== undefined) {
      return `row-${itemAsRecord._id}`;
    }
    // Fallback: create a hash-like key from the first few column values
    const keyValues = columns.slice(0, 3).map(col => {
      const value = getValue(item, col.key);
      return String(value || '').slice(0, 20);
    }).join('-');
    return `row-${index}-${keyValues}`;
  };

  return (
    <div className={`overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              {columns.map((column) => (
                <th
                  key={`column-${String(column.key)}`}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.className || ''}`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, rowIndex) => (
              <tr
                key={getRowKey(item, rowIndex)}
                className={`hover:bg-gray-50 transition-colors duration-150 ${
                  onRowClick ? 'cursor-pointer' : ''
                }`}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column) => {
                  const value = getValue(item, column.key);
                  return (
                    <td
                      key={`${getRowKey(item, rowIndex)}-${String(column.key)}`}
                      className={`px-6 py-4 whitespace-nowrap ${column.className || ''}`}
                    >
                      {column.render ? column.render(value, item) : value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Table;
